import { useState } from "react";
import { Icon } from "@iconify/react";
import { AppModal } from "@/shared/components/dialog";
import { Button } from "@/shared/components/button";
import { cn } from "@/shared/lib/utils";
import { toastError, toastSuccess } from "@/shared/lib/toast";
import { acceptIncomingPatientRequestApi, updateCaregiverAccessApi } from "@/portals/patient/api/familyApi";
import { CAREGIVER_CATEGORY_OPTIONS, type CaregiverCategoryValue, type IncomingPatientAccessRequest } from "@/shared/types/family";

interface ApproveCaregiverAccessModalProps {
  open: boolean;
  request: IncomingPatientAccessRequest | null;
  onOpenChange: (open: boolean) => void;
  onApproved: () => void;
}

const INITIAL_SELECTION = new Set<CaregiverCategoryValue>(["MedicationRequest"]);

export function ApproveCaregiverAccessModal({ open, request, onOpenChange, onApproved }: ApproveCaregiverAccessModalProps) {
  const [selectedCategories, setSelectedCategories] = useState<Set<CaregiverCategoryValue>>(() => new Set(INITIAL_SELECTION));
  const [isSubmitting, setIsSubmitting] = useState(false);

  const reset = () => setSelectedCategories(new Set(INITIAL_SELECTION));

  const close = () => {
    if (isSubmitting) return;
    onOpenChange(false);
    reset();
  };

  const toggleCategory = (category: CaregiverCategoryValue) => {
    setSelectedCategories((current) => {
      const next = new Set(current);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  };

  const allSelected = selectedCategories.size === CAREGIVER_CATEGORY_OPTIONS.length;
  const toggleSelectAll = () => {
    setSelectedCategories(allSelected ? new Set() : new Set(CAREGIVER_CATEGORY_OPTIONS.map((c) => c.value)));
  };

  const handleApprove = async () => {
    if (!request || selectedCategories.size === 0 || isSubmitting) return;

    const categories = Array.from(selectedCategories);

    setIsSubmitting(true);
    const accepted = await acceptIncomingPatientRequestApi(request.id);
    if (accepted.error) {
      setIsSubmitting(false);
      toastError(accepted.error);
      return;
    }

    const configured = await updateCaregiverAccessApi(request.id, { categories });
    setIsSubmitting(false);
    if (configured.error) {
      onApproved();
      onOpenChange(false);
      reset();
      toastError(`Access was approved, but the selected permissions could not be saved: ${configured.error}`);
      return;
    }

    onApproved();
    toastSuccess(`${request.caregiverName} now has access to the selected records.`);
    onOpenChange(false);
    reset();
  };

  return (
    <AppModal
      open={open}
      onOpenChange={(nextOpen) => { if (!nextOpen) close(); }}
      width={460}
      className={cn(
        "[&_.ant-modal-content]:!overflow-hidden [&_.ant-modal-content]:!rounded-[18px] [&_.ant-modal-content]:!p-0",
        "[&_.ant-modal-close]:!right-6 [&_.ant-modal-close]:!top-[21px] [&_.ant-modal-close]:!h-8 [&_.ant-modal-close]:!w-8",
        "[&_.ant-modal-close]:!rounded-lg [&_.ant-modal-close]:!bg-[#F6F7F9] [&_.ant-modal-close]:!text-[#8C95A4]",
        "[&_.ant-modal-close]:hover:!bg-[#EEF0F4]",
      )}
    >
      <header className="border-b border-[#E9EBEF] px-6 pb-[18px] pt-[22px] pr-16">
        <h2 className="text-[18px] font-bold leading-6 text-[#160747]">Select Records to Share</h2>
        <p className="mt-1 text-[13px] leading-5 text-[#747C8B]">
          Choose which health information you’d like to share with {request?.caregiverName ?? "this caregiver"}.
        </p>
      </header>

      <section className="px-6 pb-[18px] pt-[18px]">
        <div className="mb-[10px] flex items-center justify-between gap-3">
          <p className="text-[12px] font-extrabold uppercase leading-4 tracking-[0.075em] text-[#160747]">
            Select categories to request
          </p>
          <button
            type="button"
            onClick={toggleSelectAll}
            className="shrink-0 text-[12px] font-bold text-[#E4002B] hover:underline"
          >
            {allSelected ? "Deselect All" : "Select All"}
          </button>
        </div>
        <div className="space-y-[7px]">
          {CAREGIVER_CATEGORY_OPTIONS.map((category) => {
            const selected = selectedCategories.has(category.value);
            return (
              <button
                key={category.value}
                type="button"
                role="checkbox"
                aria-checked={selected}
                onClick={() => toggleCategory(category.value)}
                className={cn(
                  "flex h-[44px] w-full items-center gap-3 rounded-[10px] border px-[13px] text-left transition-colors",
                  selected
                    ? "border-[#E4002B] bg-[#FFF4F6]"
                    : "border-[#D8DDE6] bg-white hover:border-[#BAC2CE]",
                )}
              >
                <span
                  className={cn(
                    "flex h-[19px] w-[19px] shrink-0 items-center justify-center rounded-[5px] border-2",
                    selected ? "border-[#E4002B] bg-[#E4002B]" : "border-[#D4D9E1] bg-white",
                  )}
                >
                  {selected && <Icon icon="lucide:check" className="h-3.5 w-3.5 text-white" strokeWidth={3} />}
                </span>
                <span className={cn("text-[14px] leading-5 text-[#160747]", selected && "font-bold")}>{category.label}</span>
              </button>
            );
          })}
        </div>
      </section>

      <footer className="flex h-[77px] items-center justify-between border-t border-[#E9EBEF] px-6">
        <Button
          variant="link"
          onClick={close}
          disabled={isSubmitting}
          className="!h-auto !p-1 !text-[14px] !font-medium !text-[#98A0AE] hover:!text-[#667080]"
        >
          Cancel
        </Button>
        <Button
          onClick={handleApprove}
          disabled={selectedCategories.size === 0 || !request || isSubmitting}
          loading={isSubmitting}
          className="!h-[42px] !rounded-[10px] !border-0 !bg-[#F17D95] !px-5 !text-[14px] !font-bold !text-white hover:!bg-[#EC6B87] disabled:!bg-[#F3B8C5]"
        >
          Approve & Share
          <Icon icon="lucide:send" className="ml-1 h-[15px] w-[15px] text-white" />
        </Button>
      </footer>
    </AppModal>
  );
}
